import React from 'react';
import { ArrowLeftIcon } from '@radix-ui/react-icons';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div className="privacy-page">
      <div className="privacy-header">
        <button className="btn btn-secondary back-btn" onClick={onBack}>
          <ArrowLeftIcon style={{ marginRight: '8px' }} />
          Back
        </button>
        <h1>Privacy Policy</h1>
      </div>

      <div className="privacy-content">
        <p className="privacy-updated">Last updated: February 2026</p>

        <section className="privacy-section">
          <h2>Introduction</h2>
          <p>
            Welcome to AI Craft Studio ("we," "our," or "us"). We are committed to protecting your privacy
            and ensuring the security of your personal information. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use our AI-powered image generation services.
          </p>
          <p>
            By using AI Craft Studio, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Information We Collect</h2>

          <h3>Account Information</h3>
          <p>When you create an account through Firebase Authentication, we collect:</p>
          <ul>
            <li><strong>Email address:</strong> Used for account identification and communication</li>
            <li><strong>User ID (UID):</strong> A unique identifier for your account</li>
            <li><strong>Display name:</strong> If provided through your authentication provider</li>
            <li><strong>Profile photo URL:</strong> If provided through your authentication provider</li>
          </ul>

          <h3>Usage Data</h3>
          <p>We automatically collect certain information when you use our services:</p>
          <ul>
            <li>Credit balance and transaction history</li>
            <li>Generated images (stored temporarily as compressed thumbnails)</li>
            <li>Feature usage statistics (Arrow Vision, Person Remover, Clothing Try-On)</li>
            <li>IP address for security and abuse prevention</li>
          </ul>

          <h3>Images You Upload</h3>
          <p>
            When using our services, you may upload images for processing. These images are:
          </p>
          <ul>
            <li>Temporarily stored during processing</li>
            <li>Sent to Google's GEMINI AI for image generation</li>
            <li>Not permanently stored on our servers after processing</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>How We Use Your Information</h2>
          <p>We use the collected information for:</p>
          <ul>
            <li>Providing and maintaining our AI image generation services</li>
            <li>Managing your account and credit balance</li>
            <li>Processing your payments through Stripe</li>
            <li>Sending you service-related notifications</li>
            <li>Improving our services and user experience</li>
            <li>Detecting and preventing fraud or abuse</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Third-Party Services</h2>

          <h3>Firebase (Google)</h3>
          <p>
            We use Firebase for authentication and data storage. Firebase's privacy policy can be found at:{' '}
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
              https://firebase.google.com/support/privacy
            </a>
          </p>

          <h3>Google GEMINI AI</h3>
          <p>
            Images you upload are processed using Google's GEMINI AI. According to Google's API Terms of Service,
            images sent through the API are:
          </p>
          <ul>
            <li>Processed to generate the requested output</li>
            <li>Not used to train Google's AI models</li>
            <li>Not stored permanently by Google</li>
          </ul>
          <p>
            For more information, see Google's AI Privacy Policy:{' '}
            <a href="https://ai.google/privacy/" target="_blank" rel="noopener noreferrer">
              https://ai.google/privacy/
            </a>
          </p>

          <h3>Stripe</h3>
          <p>
            Payment processing is handled by Stripe. We do not store your credit card information.
            Stripe's privacy policy can be found at:{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
              https://stripe.com/privacy
            </a>
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Storage and Security</h2>
          <p>Your data is stored securely using the following measures:</p>
          <ul>
            <li><strong>Firestore Database:</strong> User accounts, credits, and image metadata are stored in Google Cloud Firestore with encryption at rest</li>
            <li><strong>Image Compression:</strong> Generated images are compressed (max 800KB) and stored as thumbnails. We keep a maximum of 10 recent images per user</li>
            <li><strong>Authentication:</strong> All API requests require valid Firebase authentication tokens</li>
            <li><strong>Rate Limiting:</strong> IP-based rate limiting (50 requests/day) helps prevent abuse</li>
            <li><strong>HTTPS:</strong> All data transmission is encrypted using SSL/TLS</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Data Retention</h2>
          <ul>
            <li><strong>Account data:</strong> Retained until you delete your account</li>
            <li><strong>Generated images:</strong> We store up to 10 most recent compressed thumbnails per user</li>
            <li><strong>Shared images:</strong> Temporarily stored for 24 hours for social media sharing</li>
            <li><strong>Payment records:</strong> Retained as required by law and Stripe's policies</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Request correction of inaccurate data</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
            <li><strong>Data Portability:</strong> Request your data in a machine-readable format</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at the email address provided below.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Children's Privacy</h2>
          <p>
            AI Craft Studio is not intended for children under 13 years of age. We do not knowingly collect
            personal information from children under 13. If you are a parent or guardian and believe your
            child has provided us with personal information, please contact us.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by
            posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <ul>
            <li><strong>Email:</strong> privacy@syncode.it</li>
            <li><strong>Website:</strong> https://aitools.syncode.it</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
